/**
 * Build 6B — the hooks behind the entry forms: KPI targets, condition
 * scores, funding envelopes and test thresholds.
 *
 * A save refreshes everything on the screen, not one key: a target is read by
 * the dashboard, the executive desks, the green program and the assistant
 * under their own keys, and an indicator that did not move after its figure
 * was saved is the failure these forms exist to end. The writes are rare.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "../app/SessionContext";
import type { AmConditionInput, EnvelopeInput, KpiTargetInput, ThresholdsInput } from "../domain/entryForms";

function useRefreshAll() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries();
}

export function useSetKpiTarget() {
  const { source, site } = useSession();
  const done = useRefreshAll();
  return useMutation({ mutationFn: (input: KpiTargetInput) => source.setKpiTarget(site.id, input), onSuccess: done });
}

export function useRetireKpiTarget() {
  const { source, site } = useSession();
  const done = useRefreshAll();
  return useMutation({
    mutationFn: ({ metricKey, reason }: { metricKey: string; reason: string }) =>
      source.retireKpiTarget(site.id, metricKey, reason),
    onSuccess: done,
  });
}

export function useKpiTargetHistory(metricKey: string | null) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["kpi-target-history", site.id, metricKey],
    enabled: metricKey != null,
    queryFn: () => source.kpiTargetHistory(site.id, metricKey as string),
  });
}

export function useAmRecordCondition() {
  const { source } = useSession();
  const done = useRefreshAll();
  return useMutation({
    mutationFn: ({ assetId, input }: { assetId: string; input: AmConditionInput }) =>
      source.amRecordCondition(assetId, input),
    onSuccess: done,
  });
}

export function useEnvelopes() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["envelopes", site.id],
    queryFn: () => source.listEnvelopes(site.id),
    staleTime: 60_000,
  });
}

export function useSetEnvelope() {
  const { source, site } = useSession();
  const done = useRefreshAll();
  return useMutation({ mutationFn: (input: EnvelopeInput) => source.setEnvelope(site.id, input), onSuccess: done });
}

export function useRemoveEnvelope() {
  const { source } = useSession();
  const done = useRefreshAll();
  return useMutation({ mutationFn: (id: string) => source.removeEnvelope(id), onSuccess: done });
}

export function useSetTestThresholds() {
  const { source, site } = useSession();
  const done = useRefreshAll();
  return useMutation({
    mutationFn: ({ key, input }: { key: string; input: ThresholdsInput }) =>
      source.setTestThresholds(site.id, key, input),
    onSuccess: done,
  });
}
